import { useState, useMemo } from 'react'
import {
  Box, Card, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Chip, MenuItem, Select, FormControl,
  InputLabel,
} from '@mui/material'

// Fallback demo data used when API returns empty
const DEMO_ENTRIES = [
  { id: 1, user: 'Melody Simon', action: 'Updated', resource: 'HV CHILI BNS — Apr scheduled qty', date: '2026-03-10T08:45:00Z', category: 'Planning Board' },
  { id: 2, user: 'Melody Simon', action: 'Created', resource: 'Scenario: GMI Soups — Full Volume', date: '2026-03-10T07:30:00Z', category: 'Scenarios' },
  { id: 3, user: 'Heike Allmann', action: 'Approved', resource: 'Budget 2026 — Base Plan', date: '2026-03-01T14:20:00Z', category: 'Plans' },
  { id: 4, user: 'Bill Frazier', action: 'Uploaded', resource: 'Forecast file — March 2026', date: '2026-03-08T09:15:00Z', category: 'Data Hub' },
  { id: 5, user: 'Melody Simon', action: 'Updated', resource: 'DOS Policy — Class A target', date: '2026-03-05T11:00:00Z', category: 'Admin' },
  { id: 6, user: 'Brenda Koch', action: 'Viewed', resource: 'Capacity Calendar — Mar 2026', date: '2026-03-09T16:45:00Z', category: 'Capacity' },
  { id: 7, user: 'Bill Frazier', action: 'Updated', resource: 'FINEST CHILI BNS — May scheduled qty', date: '2026-03-07T10:30:00Z', category: 'Planning Board' },
  { id: 8, user: 'Heike Allmann', action: 'Shared', resource: 'Budget 2026 plan link', date: '2026-02-28T15:00:00Z', category: 'Sharing' },
  { id: 9, user: 'Melody Simon', action: 'Created', resource: 'Rule: Min run qty for Line 1', date: '2026-03-04T08:00:00Z', category: 'Rules' },
  { id: 10, user: 'Bill Frazier', action: 'Exported', resource: 'Planning Board CSV — FBO Q2', date: '2026-03-06T14:20:00Z', category: 'Reports' },
]

function actionColor(action) {
  const map = { Created: 'success', Updated: 'primary', Approved: 'warning', Uploaded: 'info', Shared: 'secondary', Viewed: 'default', Exported: 'default' }
  return map[action] || 'default'
}

export default function AuditLog({ entries: entriesProp }) {
  const entries = Array.isArray(entriesProp) && entriesProp.length > 0
    ? entriesProp
    : DEMO_ENTRIES

  const [search, setSearch] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const users = [...new Set(entries.map((e) => e.user))]
  const categories = [...new Set(entries.map((e) => e.category))]

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      if (userFilter && entry.user !== userFilter) return false
      if (categoryFilter && entry.category !== categoryFilter) return false
      if (search) {
        const q = search.toLowerCase()
        return (
          entry.user.toLowerCase().includes(q) ||
          entry.action.toLowerCase().includes(q) ||
          entry.resource.toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [entries, search, userFilter, categoryFilter])

  const formatDateTime = (iso) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          size="small"
          placeholder="User, action, or resource…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>User</InputLabel>
          <Select label="User" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <MenuItem value="">All Users</MenuItem>
            {users.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
          {filtered.length} entries
        </Typography>
      </Box>

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(entry.date)}</Typography>
                  </TableCell>
                  <TableCell>{entry.user}</TableCell>
                  <TableCell>
                    <Chip label={entry.action} size="small" color={actionColor(entry.action)} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{entry.resource}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={entry.category} size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No audit entries match your filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  )
}
